/**
 * Subscription tier definitions — shared across frontend components
 *
 * Pricing strategy:
 *   - Annual prices are the default display prices (effectively 50% off)
 *   - Monthly prices are higher — incentivizes annual lock-in
 *   - "Crazy cheap, crazy real" — Starter tier intentionally loaded with
 *     features competitors gate behind $30-$100/mo plans. The moat is value.
 */

export const TIER_ORDER = ['trial', 'starter', 'trader', 'pro', 'elite'];

// 'pending' is a special non-feature tier for users who have signed up but
// not yet completed Stripe Checkout (no card on file). They get bounced to
// /pricing by App.jsx ProtectedRoute. NOT in TIER_ORDER because it's not a
// real product tier — keep it out of comparison tables and access-gate logic.
export const PENDING_TIER = 'pending';

export const TIER_CONFIG = {
  pending: {
    id: 'pending',
    label: 'Subscription Required',
    price: 0,
    monthlyPrice: 0,
    annualPrice: 0,
    durationDays: 0,
    order: -1,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    badgeClass: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    // These properties must be present (not undefined) so SettingsPage's
    // `tierCfg.chains.join(...)` and `tierCfg.features.someFlag` calls don't
    // crash when a pending user briefly visits /settings before completing
    // checkout. Same shape as the other tiers — values just signal "no access".
    tagline: 'Pick a plan to unlock the platform',
    maxBots: 0,
    maxWalletsPerBot: 0,
    historyDays: 0,
    chains: [],
    alertTypes: [],
    features: {},
    upgradeAdds: [],
  },
  trial: {
    id: 'trial',
    label: 'Trial',
    price: 0,
    monthlyPrice: 0,
    annualPrice: 0,
    durationDays: 14,
    order: 0,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    badgeClass: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    tagline: '14 days free — no card required for paper trading',
    maxBots: 1,
    maxWalletsPerBot: 1,
    historyDays: 7,
    strategyCount: '2 Basic',
    chains: ['Ethereum', 'Base'],
    alertTypes: ['Email'],
    features: {
      gridBot: false,
      smartTradeTerminal: false,
      dca: true,
      limitOrders: false,
      tradingViewWebhooks: false,
      dexSwap: true,
      realBacktesting: false,
      nlBotBuilder: false,
      futures: false,
      algoOrders: false,
      conditionalBots: false,
      twoWayTelegram: false,
      copyTrading: false,
      portfolioAnalytics: false,
      whaleWatcher: false,
      mexcAccess: false,
      verifiedTrackRecordSeller: false,
      stripeConnectPayouts: false,
      gasMonitor: false,
      botCloning: false,
      arbitrageDetector: false,
      portfolioRebalancing: false,
      backtesting: false,
      advancedSignals: false,
      webhookAlerts: false,
      fullMarketplace: false,
      paperTrading: true,
      copyTradingWatchOnly: true,
      aiStrategyGen: false,
      referralSystem: false,
      launchVolumeForecaster: false,
      dexLiquidityOptimizer: false,
      listingReadiness: false,
      aiRiskAnalyst: false,
      launchRoadmap: false,
      volumeHealthWidget: false,
      competitorTracker: false,
      tokenomicsAdvisor: false,
      multiAccountAggregator: false,
      teamAccounts: false,
      taxAutoHarvest: false,
      institutionalReports: false,
      aiStrategyCoach: false,
      quantLab: false,
      uniswapLpStudio: false,
      tokenLaunchScanner: false,
      cexBinance: false,
      cexBybit: false,
      cexOkx: false,
      cexFutures: false,
    },
  },

  starter: {
    id: 'starter',
    label: 'Starter',
    price: 10,
    monthlyPrice: 19.99,
    annualPrice: 10,
    order: 1,
    tagline: 'More features than competitors\' $50/mo plans. CEX + DEX from day one.',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    badgeClass: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    maxBots: 2,
    maxWalletsPerBot: 5,
    historyDays: 30,
    strategyCount: 'Grid + 5 templates',
    chains: ['Ethereum', 'Polygon', 'Arbitrum', 'Base', 'BSC', 'Avalanche', 'Optimism'],
    alertTypes: ['Email', 'Telegram', 'Discord', 'In-App'],
    quotas: {
      aiSignalsPerDay: 3,
      backtestsPerDay: 3,
      nlBotBuildsPerMonth: 5,
    },
    summary: [
      { icon: 'Globe',   label: '15 CEX exchanges + 7 DEX chains (unified)' },
      { icon: 'Grid3X3', label: 'Grid Bot + DCA + Smart Trade Terminal' },
      { icon: 'Brain',   label: 'AI Signals (3/day) + Real Backtests (3/day)' },
      { icon: 'Bot',     label: 'NL Bot Builder (5/mo) + TradingView Webhooks' },
      { icon: 'DollarSign', label: 'Pay in USDC (no card, multi-chain auto-renew)' },
      { icon: 'Sparkles',label: 'Premium chat: voice notes, trade snapshots, polls' },
      { icon: 'Star',    label: 'Verified Caller badge + Top Calls of the Week' },
      { icon: 'Shield',  label: 'Position Integrity + drift detection' },
    ],
    upgradeAdds: [
      'Perpetual Futures (8 exchanges, 125x leverage)',
      'Algo Orders (TWAP/VWAP/Iceberg/Bracket)',
      'Copy Trading + Conditional Bots + Two-Way Telegram',
    ],
    features: {
      gridBot: true,
      smartTradeTerminal: true,
      dca: true,
      limitOrders: true,
      tradingViewWebhooks: true,
      dexSwap: true,
      realBacktesting: true,
      nlBotBuilder: true,
      futures: false,
      algoOrders: false,
      conditionalBots: false,
      twoWayTelegram: false,
      copyTrading: false,
      portfolioAnalytics: false,
      whaleWatcher: false,
      mexcAccess: false,
      verifiedTrackRecordSeller: false,
      stripeConnectPayouts: false,
      gasMonitor: true,
      botCloning: false,
      arbitrageDetector: false,
      portfolioRebalancing: false,
      backtesting: true,
      advancedSignals: true,
      webhookAlerts: true,
      fullMarketplace: true,
      paperTrading: true,
      copyTradingWatchOnly: true,
      aiStrategyGen: false,
      referralSystem: true,
      launchVolumeForecaster: false,
      dexLiquidityOptimizer: false,
      listingReadiness: false,
      aiRiskAnalyst: false,
      launchRoadmap: false,
      volumeHealthWidget: false,
      competitorTracker: false,
      tokenomicsAdvisor: false,
      multiAccountAggregator: false,
      teamAccounts: false,
      taxAutoHarvest: false,
      institutionalReports: false,
      aiStrategyCoach: false,
      quantLab: false,
      uniswapLpStudio: false,
      tokenLaunchScanner: false,
      cexBinance: true,
      cexBybit: true,
      cexOkx: true,
      cexFutures: false,
    },
  },

  trader: {
    id: 'trader',
    label: 'Trader',
    price: 49.99,
    monthlyPrice: 65.99,
    annualPrice: 49.99,
    order: 2,
    popular: true,
    tagline: 'Pro-level execution: futures, algos, conditional bots, copy trading.',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    badgeClass: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
    maxBots: 5,
    maxWalletsPerBot: 10,
    historyDays: 90,
    strategyCount: 'Advanced + Custom',
    chains: ['Ethereum', 'Polygon', 'Arbitrum', 'Base', 'BSC', 'Avalanche', 'Optimism'],
    alertTypes: ['Email', 'Telegram', 'Discord', 'Webhook', 'In-App'],
    quotas: {
      aiSignalsPerDay: 50,
      backtestsPerDay: 50,
      nlBotBuildsPerMonth: 50,
    },
    summary: [
      { icon: 'Zap',       label: 'Everything in Starter' },
      { icon: 'TrendingUp',label: 'Perpetual Futures (8 exchanges, 125x leverage)' },
      { icon: 'Layers',    label: 'Algo Orders (TWAP/VWAP/Iceberg/Bracket OCO)' },
      { icon: 'GitBranch', label: 'Conditional / Composite Bots (regime-switching)' },
      { icon: 'Bot',       label: 'Two-Way Telegram Trading (/buy /sell /close)' },
      { icon: 'Copy',      label: 'Copy Trading (follow up to 10 traders)' },
      { icon: 'Brain',     label: 'AI Signals (50/day) + Backtests (50/day)' },
      { icon: 'DollarSign',label: 'Dual-View Accounting (operational + tax FIFO/LIFO/HIFO)' },
    ],
    upgradeAdds: [
      'Create Paid Channels (earn 80% USDC from subscribers)',
      'Portfolio Analytics (8-tab Bloomberg dashboard)',
      'AI Risk Analyst + Whale Watcher + MEXC',
    ],
    features: {
      gridBot: true,
      smartTradeTerminal: true,
      dca: true,
      limitOrders: true,
      tradingViewWebhooks: true,
      dexSwap: true,
      realBacktesting: true,
      nlBotBuilder: true,
      futures: true,
      algoOrders: true,
      conditionalBots: true,
      twoWayTelegram: true,
      copyTrading: true,
      portfolioAnalytics: false,
      whaleWatcher: false,
      mexcAccess: false,
      verifiedTrackRecordSeller: false,
      stripeConnectPayouts: false,
      gasMonitor: true,
      botCloning: true,
      arbitrageDetector: false,
      portfolioRebalancing: false,
      backtesting: true,
      advancedSignals: true,
      webhookAlerts: true,
      fullMarketplace: true,
      paperTrading: true,
      copyTradingWatchOnly: true,
      aiStrategyGen: false,
      referralSystem: true,
      launchVolumeForecaster: false,
      dexLiquidityOptimizer: false,
      listingReadiness: false,
      aiRiskAnalyst: false,
      launchRoadmap: false,
      volumeHealthWidget: false,
      competitorTracker: false,
      tokenomicsAdvisor: false,
      multiAccountAggregator: false,
      teamAccounts: false,
      taxAutoHarvest: false,
      institutionalReports: false,
      aiStrategyCoach: false,
      quantLab: false,
      uniswapLpStudio: false,
      tokenLaunchScanner: false,
      cexBinance: true,
      cexBybit: true,
      cexOkx: true,
      cexFutures: true,
    },
  },

  pro: {
    id: 'pro',
    label: 'Pro',
    price: 75.99,
    monthlyPrice: 99.99,
    annualPrice: 75.99,
    order: 3,
    tagline: 'Monetize your skill: create Paid Channels, sell strategies, full pro analytics.',
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/10',
    borderColor: 'border-violet-500/30',
    badgeClass: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
    maxBots: 10,
    maxWalletsPerBot: 25,
    historyDays: 365,
    strategyCount: 'All + Custom',
    chains: ['Ethereum', 'Polygon', 'Arbitrum', 'Base', 'BSC', 'Avalanche', 'Optimism', 'Solana'],
    alertTypes: ['Email', 'Telegram', 'Discord', 'Webhook', 'SMS', 'In-App'],
    quotas: {
      aiSignalsPerDay: 'unlimited',
      backtestsPerDay: 500,
      nlBotBuildsPerMonth: 'unlimited',
    },
    summary: [
      { icon: 'Crown',      label: 'Everything in Trader' },
      { icon: 'DollarSign', label: 'Create Paid Channels (earn 80% USDC from subscribers)' },
      { icon: 'PieChart',   label: 'Portfolio Analytics (8-tab Bloomberg dashboard)' },
      { icon: 'Eye',        label: 'Whale Watcher + Smart Money tracking' },
      { icon: 'Brain',      label: 'AI Risk Analyst' },
      { icon: 'Lock',       label: 'MEXC integration (high-leverage)' },
      { icon: 'Shield',     label: 'Verified Track Record — sell on Marketplace' },
      { icon: 'Copy',       label: 'Copy Trading (unlimited follows)' },
    ],
    upgradeAdds: [
      '17 institutional Elite tools (Trader\'s Suite + Issuer Tools + Tokenomics)',
      'Multi-Account Aggregator + AI Strategy Coach + Quant Lab',
      'Stripe Connect payouts + Direct founder access + Whale Club',
    ],
    features: {
      gridBot: true,
      smartTradeTerminal: true,
      dca: true,
      limitOrders: true,
      tradingViewWebhooks: true,
      dexSwap: true,
      realBacktesting: true,
      nlBotBuilder: true,
      futures: true,
      algoOrders: true,
      conditionalBots: true,
      twoWayTelegram: true,
      copyTrading: true,
      portfolioAnalytics: true,
      whaleWatcher: true,
      mexcAccess: true,
      verifiedTrackRecordSeller: true,
      stripeConnectPayouts: false,
      gasMonitor: true,
      botCloning: true,
      arbitrageDetector: true,
      portfolioRebalancing: true,
      backtesting: true,
      advancedSignals: true,
      webhookAlerts: true,
      fullMarketplace: true,
      paperTrading: true,
      copyTradingWatchOnly: true,
      aiStrategyGen: true,
      referralSystem: true,
      launchVolumeForecaster: false,
      dexLiquidityOptimizer: false,
      listingReadiness: false,
      aiRiskAnalyst: true,
      launchRoadmap: false,
      volumeHealthWidget: false,
      competitorTracker: false,
      tokenomicsAdvisor: false,
      multiAccountAggregator: false,
      teamAccounts: false,
      taxAutoHarvest: false,
      institutionalReports: false,
      aiStrategyCoach: false,
      quantLab: false,
      uniswapLpStudio: false,
      tokenLaunchScanner: false,
      cexBinance: true,
      cexBybit: true,
      cexOkx: true,
      cexFutures: true,
    },
  },

  elite: {
    id: 'elite',
    label: 'Elite',
    price: 149.99,
    monthlyPrice: 199.99,
    annualPrice: 149.99,
    order: 4,
    exclusive: true,
    tagline: '17 institutional tools no other platform has. Built for serious professionals + token issuers.',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/30',
    badgeClass: 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/40',
    maxBots: 25,
    maxWalletsPerBot: 50,
    historyDays: 'unlimited',
    strategyCount: 'All + Exclusive',
    chains: ['Ethereum', 'Polygon', 'Arbitrum', 'Base', 'BSC', 'Avalanche', 'Optimism', 'Solana', 'All Future Chains'],
    alertTypes: ['Email', 'Telegram', 'Discord', 'Webhook', 'SMS', 'In-App', 'Custom'],
    quotas: {
      aiSignalsPerDay: 'unlimited',
      backtestsPerDay: 'unlimited',
      nlBotBuildsPerMonth: 'unlimited',
    },
    summary: [
      { icon: 'Crown',     label: 'Everything in Pro · Unlimited everything' },
      { icon: 'Layers',    label: 'Multi-Account Aggregator (CEX + DEX unified)' },
      { icon: 'Brain',     label: 'AI Strategy Coach + Quant Lab' },
      { icon: 'PieChart',  label: 'Institutional Reports (Bloomberg-grade PDFs)' },
      { icon: 'Eye',       label: 'Token Launch Scanner + Whale Club access' },
      { icon: 'Building2', label: 'Tokenomics Studio + Advisor (full issuer suite)' },
      { icon: 'TrendingUp',label: 'Launch Roadmap + Volume Forecaster + Listing Readiness' },
      { icon: 'DollarSign',label: 'Stripe Connect creator payouts + Direct founder access' },
    ],
    upgradeAdds: [],
    features: {
      gridBot: true,
      smartTradeTerminal: true,
      dca: true,
      limitOrders: true,
      tradingViewWebhooks: true,
      dexSwap: true,
      realBacktesting: true,
      nlBotBuilder: true,
      futures: true,
      algoOrders: true,
      conditionalBots: true,
      twoWayTelegram: true,
      copyTrading: true,
      portfolioAnalytics: true,
      whaleWatcher: true,
      mexcAccess: true,
      verifiedTrackRecordSeller: true,
      stripeConnectPayouts: true,
      gasMonitor: true,
      botCloning: true,
      arbitrageDetector: true,
      portfolioRebalancing: true,
      backtesting: true,
      advancedSignals: true,
      webhookAlerts: true,
      fullMarketplace: true,
      paperTrading: true,
      copyTradingWatchOnly: true,
      aiStrategyGen: true,
      referralSystem: true,
      launchVolumeForecaster: true,
      dexLiquidityOptimizer: true,
      listingReadiness: true,
      aiRiskAnalyst: true,
      launchRoadmap: true,
      volumeHealthWidget: true,
      competitorTracker: true,
      tokenomicsAdvisor: true,
      multiAccountAggregator: true,
      teamAccounts: true,
      taxAutoHarvest: true,
      institutionalReports: true,
      aiStrategyCoach: true,
      quantLab: true,
      uniswapLpStudio: true,
      tokenLaunchScanner: true,
      cexBinance: true,
      cexBybit: true,
      cexOkx: true,
      cexFutures: true,
    },
  },
};

/**
 * Full feature matrix for the detail modal.
 * Each row: { key, label, description, tiers: { starter, trader, pro, elite } }
 * Values: true = ✓, false = ✗, string = limit/note (e.g. "3/day").
 */
export const FEATURE_MATRIX = [
  // ─── Trading
  { category: 'Trading', key: 'gridBot', label: 'Grid Bot', description: 'Place N buy/sell orders in a price range — profits from oscillation.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'smartTradeTerminal', label: 'Smart Trade Terminal', description: 'Combined entry + multi-TP + trailing SL panel.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'dca', label: 'DCA Scheduler', description: 'Recurring swaps on autopilot (e.g. $100 ETH every Monday).', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'limitOrders', label: 'Limit Orders (DEX & CEX)', description: 'Place limit orders on any DEX token pair or CEX market.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'dexSwap', label: 'DEX Swap (7 chains)', description: 'MEV-protected swap router across 7 chains.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'cexSpot', label: 'CEX Spot Trading (15 exchanges)', description: 'Direct spot trading on Binance, Coinbase, Kraken + 12 more.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'futures', label: 'Perpetual Futures (8 exchanges, 125x)', description: 'Trade perps across 8 exchanges with up to 125x leverage.', tiers: { starter: false, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'mexcAccess', label: 'MEXC integration', description: 'High-leverage exchange. You assume all risk per disclaimer.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Trading', key: 'algoOrders', label: 'Algo Orders (TWAP/VWAP/Iceberg/Bracket)', description: 'Institutional execution algorithms.', tiers: { starter: false, trader: true, pro: true, elite: true } },
  { category: 'Trading', key: 'tradingViewWebhooks', label: 'TradingView Webhooks', description: 'Pipe Pine Script alerts directly into haythix for auto-execution.', tiers: { starter: true, trader: true, pro: true, elite: true } },

  // ─── Bots & Automation
  { category: 'Bots & Automation', key: 'nlBotBuilder', label: 'NL Bot Builder', description: 'Describe a strategy in plain English; AI builds the config.', tiers: { starter: '5/month', trader: '50/month', pro: 'unlimited', elite: 'unlimited' } },
  { category: 'Bots & Automation', key: 'realBacktesting', label: 'Real Historical Backtesting', description: 'Real OHLCV data backtest — not simulated.', tiers: { starter: '3/day', trader: '50/day', pro: '500/day', elite: 'unlimited' } },
  { category: 'Bots & Automation', key: 'conditionalBots', label: 'Conditional / Composite Bots', description: 'Chain conditions: if regime → switch strategy.', tiers: { starter: false, trader: true, pro: true, elite: true } },
  { category: 'Bots & Automation', key: 'twoWayTelegram', label: 'Two-Way Telegram Bot', description: 'Trade via Telegram commands (/buy, /close-all, etc.).', tiers: { starter: false, trader: true, pro: true, elite: true } },
  { category: 'Bots & Automation', key: 'botCloning', label: 'Bot Cloning', description: 'Duplicate any bot configuration with one click.', tiers: { starter: false, trader: true, pro: true, elite: true } },

  // ─── Intelligence & AI
  { category: 'Intelligence & AI', key: 'advancedSignals', label: 'AI Signals', description: 'AI-generated buy/sell signals with confidence scores.', tiers: { starter: '3/day', trader: '50/day', pro: 'unlimited', elite: 'unlimited' } },
  { category: 'Intelligence & AI', key: 'forecasting', label: 'AI Forecasting', description: 'Multi-horizon price forecasts.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Intelligence & AI', key: 'marketTrends', label: 'Market Trend Analysis', description: 'Real-time regime detection (trending/ranging/volatile).', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Intelligence & AI', key: 'aiRiskAnalyst', label: 'AI Risk Analyst', description: 'Pre-trade analysis with risk score 1-10.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Intelligence & AI', key: 'whaleWatcher', label: 'Whale Watcher', description: 'Smart-money tracking + accumulation trends.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Intelligence & AI', key: 'portfolioAnalytics', label: 'Portfolio Analytics (8 tabs)', description: 'Bloomberg-grade institutional dashboard.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Intelligence & AI', key: 'tokenomicsAdvisor', label: 'AI Tokenomics Advisor', description: 'Project tokenomics analysis & recommendations.', tiers: { starter: false, trader: false, pro: false, elite: true } },

  // ─── Community & Social
  { category: 'Community & Social', key: 'communityChat', label: 'Community Chat (all rooms)', description: 'DEX networks, CEX exchange rooms, trading rooms.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'usdcTipping', label: 'USDC Tipping', description: 'Send/receive USDC tips on Polygon, Base, Arbitrum, Ethereum.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'verifiedCallTracking', label: 'Verified Call Tracking', description: 'Auto-tracked trade calls at 1h/24h/7d checkpoints.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'copyTrading', label: 'Copy Trading', description: 'Auto-replicate top traders’ positions.', tiers: { starter: 'watch only', trader: 'follow 10', pro: 'unlimited', elite: 'unlimited' } },
  { category: 'Community & Social', key: 'fullMarketplace', label: 'Strategy Marketplace (buyer)', description: 'Browse + buy verified strategies from creators.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'verifiedTrackRecordSeller', label: 'Verified Track Record (seller)', description: 'List your strategies with on-chain proof of P&L.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Community & Social', key: 'stripeConnectPayouts', label: 'Stripe Connect Payouts', description: 'Earn revenue share on strategies sold.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Community & Social', key: 'eliteLounge', label: 'Elite Lounge + Whale Club', description: 'Direct founder access, weekly AMA, private alpha.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Community & Social', key: 'paidChannels', label: 'Create Paid Channels (earn USDC)', description: '1-3 tier subscriptions in USDC. 80% creator / 20% platform. Instant on-chain payouts on Base/Polygon/Arbitrum.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Community & Social', key: 'tradeSnapshots', label: 'Server-Verified Trade Snapshots', description: 'Share trade cards with auto-detected entry/exit/PnL. Backend re-computes — no client-side fakery.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'voiceNotes', label: 'Voice Notes in Chat (30s)', description: 'Drop 30-second audio takes during volatility.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'topCallsWeek', label: 'Top Calls of the Week', description: 'Auto-pinned per-channel leaderboard ranked by % gain.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'hotChannelBadge', label: 'Hot Channel Badge', description: '🔥 HOT pill when room hits 10+ msgs/min.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'typingIndicators', label: 'Typing Indicators', description: '"Sarah is typing…" — rooms feel alive.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'tipReadReceipts', label: 'Tip Read Receipts', description: 'See "✓ Recipient saw your $5" the moment they read it.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'autoCallDetection', label: 'Auto Trade-Call Detection', description: 'Type "BUY $ETH target $3000" — we auto-capture entry + track accuracy.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Community & Social', key: 'verifiedCallerBadge', label: 'Verified Caller Badge', description: 'Earned at 20 calls @ 60%+ accuracy. Real track record, not influencer claims.', tiers: { starter: true, trader: true, pro: true, elite: true } },

  // ─── Crypto-Native Payments
  { category: 'Crypto-Native Payments', key: 'oneClickUsdcPay', label: 'One-Click USDC Pay', description: 'Single wallet confirmation — no card, no Stripe, no KYC.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Crypto-Native Payments', key: 'multiChainPayments', label: 'Multi-Chain (Base / Polygon / Arbitrum)', description: 'Pay from whichever chain has your USDC. Mainnet refused (gas too high).', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Crypto-Native Payments', key: 'onChainAutoRenew', label: 'On-Chain Auto-Renew (USDC allowance)', description: 'Approve once for 3/6/12 months, we auto-debit via transferFrom(). Revoke any time.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Crypto-Native Payments', key: 'renewalReminders', label: 'T-3d & T-1d Renewal Reminders', description: 'Email + in-app pings before any subscription expires.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Crypto-Native Payments', key: 'serverVerifiedPayments', label: 'Server-Verified On-Chain Payments', description: 'Every payment verified on-chain (amount + token + recipient) before access unlocks.', tiers: { starter: true, trader: true, pro: true, elite: true } },

  // ─── Engineering Integrity (transparency / honesty as a feature)
  { category: 'Engineering Integrity', key: 'positionIntegrity', label: 'Position Integrity + Drift Detection', description: 'Live balance verification + reconciliation across exchange & on-chain. Most rigorous in retail crypto.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Engineering Integrity', key: 'dualViewAccounting', label: 'Dual-View Accounting (operational + tax)', description: 'Two parallel ledgers — per-strategy native pricing vs pooled FIFO/LIFO/HIFO for taxes.', tiers: { starter: false, trader: true, pro: true, elite: true } },
  { category: 'Engineering Integrity', key: 'outboundWebhooks', label: 'Outbound Webhooks (Discord/Telegram)', description: 'Fire trade events to your Discord channel or Telegram bot. Build audience anywhere.', tiers: { starter: false, trader: true, pro: true, elite: true } },
  { category: 'Engineering Integrity', key: 'noSyntheticScenarios', label: 'Real Backtests Only (no fake scenarios)', description: 'Stress tests use only real historical periods (LUNA, FTX, COVID). No "imagine if BTC drops 50%" theater.', tiers: { starter: true, trader: true, pro: true, elite: true } },

  // ─── Risk & Analytics
  { category: 'Risk & Analytics', key: 'paperTrading', label: 'Paper Trading', description: 'Test strategies risk-free with real order book depth.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Risk & Analytics', key: 'riskIntelligence', label: 'Risk Intelligence Center', description: 'Cross-bot exposure, drawdown, correlation.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Risk & Analytics', key: 'gasMonitor', label: 'Gas Price Monitor', description: 'Real-time gas tracking across all chains.', tiers: { starter: true, trader: true, pro: true, elite: true } },
  { category: 'Risk & Analytics', key: 'arbitrageDetector', label: 'Cross-Exchange Arbitrage', description: 'Detect price gaps between exchanges.', tiers: { starter: false, trader: false, pro: true, elite: true } },
  { category: 'Risk & Analytics', key: 'portfolioRebalancing', label: 'Portfolio Rebalancing', description: 'Automatic rebalance to target allocation.', tiers: { starter: false, trader: false, pro: true, elite: true } },

  // ─── Elite Killer Features
  { category: "Elite Trader's Suite", key: 'multiAccountAggregator', label: 'Multi-Account & Multi-Wallet Aggregator', description: 'Every connected CEX + on-chain wallet in one unified portfolio. Live balances across 4 EVM chains, cross-venue P&L, asset exposure across all venues.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'teamAccounts', label: 'Multi-User Team Accounts (Boutique / Trading Desk / Institutional)', description: '3 / 5 / 10-seat plans with role-based access (admin, trader, read-only). Shared bots, exchanges, wallets, reports. $200 setup + $100/seat/mo annual.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'taxAutoHarvest', label: 'Tax-Loss Auto-Harvest', description: 'Scans your lots for harvestable losses, suggests wash-sale-safe replacement assets, executes the sell + buy in one click. 30-day wash-sale risk warning included.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'institutionalReports', label: 'Institutional Reports (PDF)', description: 'Monthly P&L, quarterly performance, year-end tax pack (Form 8949–style), and custom date-range reports. Generated server-side as branded PDFs with full audit trail.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'aiStrategyCoach', label: 'AI Strategy Coach', description: 'Chat with haythix AI about your actual strategies. Grounded in your real bot configs and P&L — suggests parameter tweaks, flags regime mismatches, no buy/sell advice.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'quantLab', label: 'Quant Lab', description: 'Parameter sweeps, walk-forward optimization, Monte Carlo robustness, multi-symbol backtests, side-by-side strategy comparison. The anti-overfitting toolkit serious quants use.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'uniswapLpStudio', label: 'Uniswap V3 LP Studio', description: 'Concentrated-liquidity position manager. Pulls your V3 positions on-chain across 5 chains, shows in-range/out-of-range status, unclaimed fees, USD value, and simulates capital-efficiency for any proposed new range.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: "Elite Trader's Suite", key: 'tokenLaunchScanner', label: 'Token Launch Scanner', description: 'Pre-trade safety screen for new tokens. Bytecode, ownership, holder concentration, liquidity, age, suspicious functions — combined into a 0-100 safety score with per-check breakdown. Replaces the 5 sites memecoin traders juggle.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Elite Killer Features', key: 'dexLiquidityOptimizer', label: 'DEX Liquidity Depth Optimizer', description: 'Compute exact Uniswap V2/V3 liquidity needed to support your target daily volume at chosen slippage. Real math, no estimates.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Elite Killer Features', key: 'listingReadiness', label: 'Listing Readiness Scorecard', description: 'Score your token against the public listing criteria of Binance, Coinbase, OKX, Kraken, and Gemini. On-chain + CoinGecko + self-attestation.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Elite Killer Features', key: 'launchVolumeForecaster', label: 'Launch Volume Forecaster', description: 'Forecast realistic organic daily volume using real CoinGecko data from comparable tokens in your sector + market cap range.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Elite Killer Features', key: 'launchRoadmap', label: 'Launch Roadmap Planner', description: 'Plan token launches across phased timelines with real milestone tracking (marketing, listings, audits, partnerships, treasury moves).', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Elite Killer Features', key: 'volumeHealthWidget', label: 'Volume Health Widget (embeddable)', description: 'Embed live volume health on your site.', tiers: { starter: false, trader: false, pro: false, elite: true } },
  { category: 'Elite Killer Features', key: 'competitorTracker', label: 'Competitor Activity Tracker', description: 'Monitor competitor token activity in real time.', tiers: { starter: false, trader: false, pro: false, elite: true } },

  // ─── Support & Limits
  { category: 'Support & Limits', key: 'historyDays', label: 'Trade history retention', description: 'How long historical trade data is kept.', tiers: { starter: '30 days', trader: '90 days', pro: '1 year', elite: 'unlimited' } },
  { category: 'Support & Limits', key: 'alertChannels', label: 'Alert delivery channels', description: 'Where alerts can be sent.', tiers: { starter: 'Email + Telegram + Discord', trader: '+ Webhook', pro: '+ SMS', elite: '+ Custom' } },
  { category: 'Support & Limits', key: 'support', label: 'Support level', description: 'How fast we respond.', tiers: { starter: 'Standard', trader: 'Priority', pro: 'Priority', elite: 'Direct founder' } },
];

export function getTierConfig(tier) {
  return TIER_CONFIG[tier] || TIER_CONFIG.starter;
}

/** Returns numeric tier level for comparisons (higher = better) */
export function getTierLevel(tier) {
  const cfg = TIER_CONFIG[tier];
  return cfg ? cfg.order : 0;
}

/** Returns true if userTier meets or exceeds requiredTier */
export function hasAccessToTier(userTier, requiredTier) {
  return getTierLevel(userTier) >= getTierLevel(requiredTier);
}

/** Returns tier configs the user can upgrade to */
export function getUpgradeOptions(currentTier) {
  const currentLevel = getTierLevel(currentTier);
  return TIER_ORDER
    .filter(id => id !== 'trial')
    .map(id => TIER_CONFIG[id])
    .filter(t => t.order > currentLevel);
}

/** Check if a user's tier grants access to a specific feature */
export function userHasFeature(userTier, featureKey) {
  const cfg = TIER_CONFIG[userTier];
  if (!cfg) return false;
  return !!cfg.features[featureKey];
}

/** Returns the minimum tier required for a feature */
export function getRequiredTier(featureKey) {
  for (const id of TIER_ORDER) {
    if (TIER_CONFIG[id]?.features[featureKey]) return id;
  }
  return null;
}

/** Returns true if the user is currently on an active free trial */
export function isTrial(tier) {
  return tier === 'trial';
}

/** Returns days remaining in trial given trialEndsAt ISO string. Negative = expired. */
export function trialDaysRemaining(trialEndsAt) {
  if (!trialEndsAt) return null;
  return Math.ceil((new Date(trialEndsAt) - Date.now()) / (1000 * 60 * 60 * 24));
}
